'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterSectionProps {
  selectedType: string;
  selectedTopic: string;
  searchQuery: string;
  onTypeChange: (type: string) => void;
  onTopicChange: (topic: string) => void;
  onSearchChange: (query: string) => void;
  onClearFilters: () => void;
}

const FilterSection = ({
  selectedType,
  selectedTopic,
  searchQuery,
  onTypeChange,
  onTopicChange,
  onSearchChange,
  onClearFilters,
}: FilterSectionProps) => {
  const resourceTypes = [
    'All Types',
    'Guide',
    'Webinar',
    'Case Study',
    'Template',
    'Article',
  ];

  const topics = [
    'All Topics',
    'AI Adoption',
    'Change Management',
    'Organizational Readiness',
    'AI Implementation',
    'Leadership',
    'ROI Framework',
  ];

  const hasActiveFilters = selectedType !== 'All Types' || selectedTopic !== 'All Topics' || searchQuery !== '';

  return (
    <div className="bg-card rounded-lg shadow-card p-6 mb-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
        <div className="flex-1">
          <label htmlFor="search" className="block text-sm font-medium text-foreground mb-2">
            Search Resources
          </label>
          <div className="relative">
            <Icon
              name="MagnifyingGlassIcon"
              size={20}
              variant="outline"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              id="search"
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by title or description..."
              className="w-full pl-10 pr-4 py-2.5 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
            />
          </div>
        </div>

        <div className="flex-1">
          <label htmlFor="type" className="block text-sm font-medium text-foreground mb-2">
            Content Type
          </label>
          <div className="relative">
            <Icon
              name="FunnelIcon"
              size={20}
              variant="outline"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <select
              id="type"
              value={selectedType}
              onChange={(e) => onTypeChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm appearance-none bg-background"
            >
              {resourceTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <Icon
              name="ChevronDownIcon"
              size={20}
              variant="outline"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
          </div>
        </div>

        <div className="flex-1">
          <label htmlFor="topic" className="block text-sm font-medium text-foreground mb-2">
            Topic
          </label>
          <div className="relative">
            <Icon
              name="TagIcon"
              size={20}
              variant="outline"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <select
              id="topic"
              value={selectedTopic}
              onChange={(e) => onTopicChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm appearance-none bg-background"
            >
              {topics.map((topic) => (
                <option key={topic} value={topic}>
                  {topic}
                </option>
              ))}
            </select>
            <Icon
              name="ChevronDownIcon"
              size={20}
              variant="outline"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
          </div>
        </div>

        {hasActiveFilters && (
          <div className="flex items-end">
            <button
              onClick={onClearFilters}
              className="px-4 py-2.5 rounded-md font-medium text-sm text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-300 flex items-center space-x-2 border border-border"
            >
              <Icon name="XMarkIcon" size={16} variant="outline" />
              <span>Clear Filters</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterSection;